/* ==================================================================
   NOSH - PASS F2 CART BEHAVIOUR
   ==================================================================
   The merchant found any automatic drawer opening disruptive.
   Approved A5.1 behaviour for every successful add:

     -> cart sections and count update immediately
     -> drawer does NOT force itself open
     -> the existing compact polite confirmation appears
     -> browsing, scrolling and product rails remain usable
     -> a deliberate cart-icon or View cart action still opens the drawer

   HOW THIS IS DONE WITHOUT TOUCHING DAWN
   assets/product-form.js is checksum-locked in AGENTS.md and is not
   edited. Dawn's own flow is left completely intact: product-form
   posts to /cart/add, handles its own errors, and on success calls
   CartDrawer.renderContents(), which re-renders the cart sections and
   then calls open().

   This file wraps renderContents only. For each successful add it
   shadows open() with a one-shot own-property no-op that deletes itself the
   first time it is called, so the prototype method is restored
   immediately afterwards and a manual cart-icon click still opens the
   drawer normally. A 1.2s failsafe removes the shadow even if open()
   is never reached.

   Deliberately NOT intercepted:
     - failed adds. product-form returns before renderContents on an
       error, so error messages are never suppressed or replaced.
     - quantity changes and removals. Those go through CartItems /
       cart-drawer-items, a different element with a different
       renderContents, which this file never touches.
     - the cart icon, the cart count bubble, the cart page, checkout,
       the dynamic checkout button and Razorpay. None are referenced.
   ================================================================== */

(function () {
  'use strict';

  var VERSION = 2;
  if (window.NoshCart && window.NoshCart.version === VERSION) return;

  var HIDE_AFTER = 4200;

  var toast = null;
  var hideTimer = null;

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function drawerIsOpen() {
    var d = drawerEl();
    return !!(d && d.classList.contains('active'));
  }

  function buildToast() {
    if (toast && toast.isConnected) return toast;
    toast = document.createElement('div');
    toast.className = 'nosh-added';
    toast.setAttribute('data-nosh-added', '');
    // Polite status, never assertive: it must not interrupt a screen
    // reader mid-sentence, and it must never take focus away from the
    // control the customer just used.
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    var text = document.createElement('span');
    text.className = 'nosh-added__text';
    text.textContent = 'Added to cart';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'nosh-added__link';
    btn.setAttribute('data-nosh-added-open-cart', '');
    btn.textContent = 'View cart';
    btn.addEventListener('click', function () {
      hideToast();
      var d = drawerEl();
      if (d && typeof d.open === 'function') d.open();
    });

    toast.appendChild(text);
    toast.appendChild(btn);
    document.body.appendChild(toast);
    return toast;
  }

  function stickyAtcVisible() {
    var s = document.querySelector('.nosh-satc');
    if (!s) return false;
    var cs = window.getComputedStyle(s);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    return s.getBoundingClientRect().height > 0;
  }

  function showToast() {
    // If the customer already has the drawer open, the drawer itself is
    // the confirmation. A second message would be a duplicate.
    if (drawerIsOpen()) return;

    var el = buildToast();
    if (stickyAtcVisible()) el.setAttribute('data-nosh-added-lift', '');
    else el.removeAttribute('data-nosh-added-lift');

    // Re-announce on a repeat add: resetting the text makes the live
    // region fire again rather than sitting silently unchanged.
    var text = el.querySelector('.nosh-added__text');
    if (text) {
      text.textContent = '';
      window.setTimeout(function () {
        text.textContent = 'Added to cart';
      }, 40);
    }

    el.setAttribute('data-nosh-added-open', '');
    if (hideTimer) window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(hideToast, HIDE_AFTER);
  }

  function hideToast() {
    if (hideTimer) {
      window.clearTimeout(hideTimer);
      hideTimer = null;
    }
    if (toast) toast.removeAttribute('data-nosh-added-open');
  }

  // Hide the confirmation as soon as the customer opens the cart by
  // any route, so the two are never on screen together.
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('#cart-icon-bubble, .header__icon--cart')) hideToast();
    },
    true
  );

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') hideToast();
  });

  var patched = false;

  function patch() {
    if (patched) return true;
    var Ctor = window.customElements && customElements.get('cart-drawer');
    if (!Ctor || !Ctor.prototype) return false;
    var proto = Ctor.prototype;
    if (proto.__noshF2Patched) {
      patched = true;
      return true;
    }
    var original = proto.renderContents;
    if (typeof original !== 'function') return false;

    proto.__noshF2Patched = true;
    patched = true;

    proto.renderContents = function (parsedState) {
      var self = this;

      if (typeof this.open === 'function') {
        // One-shot own-property shadow. Calling it removes itself, so
        // the very next open() resolves to the prototype method again.
        this.open = function () {
          try {
            delete self.open;
          } catch (e) {
            self.open = Object.getPrototypeOf(self).open;
          }
        };
        window.setTimeout(function () {
          if (Object.prototype.hasOwnProperty.call(self, 'open')) {
            try {
              delete self.open;
            } catch (e) {
              /* prototype method is still reachable */
            }
          }
        }, 1200);
      }

      var result;
      try {
        result = original.apply(this, arguments);
      } catch (err) {
        // Never let this wrapper swallow a Dawn failure. Restore the
        // prototype method and rethrow so the failure stays visible.
        try {
          delete self.open;
        } catch (e) {
          /* ignore */
        }
        throw err;
      }

      showToast();

      return result;
    };

    return true;
  }

  if (window.customElements && customElements.whenDefined) {
    customElements
      .whenDefined('cart-drawer')
      .then(patch)
      .catch(function () {});
  }

  // Fallback for load orders where whenDefined resolves before this
  // file runs, or where the element is registered late.
  if (!patch()) {
    var tries = 0;
    var poll = window.setInterval(function () {
      tries++;
      if (patch() || tries > 40) window.clearInterval(poll);
    }, 100);
  }

  window.NoshCart = {
    version: VERSION,
    isPatched: function () {
      return patched;
    },
    suppressesAutomaticOpen: true,
    showToast: showToast,
    hideToast: hideToast,
  };
})();